import React, {useContext} from 'react';

import {ArrowBackIos} from '@mui/icons-material';
import {
  Divider,
  Fade,
  SvgIcon,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  useSelector,
} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {
  getMinDate,
  VIEW_STATE,
} from './index.js';

import ApptsContext from './ApptsContext.js';
import DebugFooter from './DebugFooter.js';
import {
  EventContainer,
  LoadingContainer,
  EventStack,
  DateSection,
  SidebarContainer,
  MainContainer,
  NavButton,
  TypographyH5,
} from './Style.js';
import VotingFooter from './VotingFooter.js';

import {ReactComponent as ChevronLeftFilled} from 'assets/svg/chevron-left-filled.svg';
import ApptInfo from 'Components/ApptInfo/index.js';
import {toDateTime} from 'Components/BookingBlocksWidget/utils.js';
import BookingDatePicker from 'Components/BookingDayPicker';
import BookingFinalizer from 'Components/BookingForm';
import BookingFormContext from 'Components/BookingForm/BookingFormContext.js';
import DatesWindow from 'Components/DatesWindow';
import MessageDisplay from 'Components/MessageDisplay';
import ErrorDisplay from 'Components/MessageDisplay/ErrorDisplay.js';
import PolicyTerms from 'Components/PolicyTerms/index.js';
import PoweredBy from 'Components/PoweredBy/index.js';
import {BreadCrumb} from 'Components/Reschedulings/Style.js';
import TimeZonePicker from 'Components/TimeZonePicker/TimeZonePicker.js';
import ZmSpinner from 'Components/ZmSpinner';
import HostContext from 'HostContext.js';
import {selectMonthAvailability} from 'Store/BookAppointmentsStore.js';
import {
  getApptEndDate,
  getDayClassName,
  getWeekAvailabilityKey,
  isAnonymousTeamAppt,
  isInApptSpanRange,
  isTeamAppt,
} from 'Utils/apptAvailabilityUtils.js';
import {EVENT_STATUS} from 'Utils/consts';
import {isCciCalendar} from 'Utils/index.js';

import 'react-datepicker/dist/react-datepicker.css';


/**
 * Appointments Page Component for Mobile Devices
 * @param {Object} props
 * @param {String} props.calendarId
 * @param {String} props.appointmentId
 * @return {ReactElement}
 */
export default function ApptsMobile(props) {
  const {
    calendarId,
    errorInfo,
    handleSelectDate,
    handleSelectSlot,
    isBookerVoting,
    onMonthChange,
    selectedSlot,
    setCurrView,
    setTroubleShootingMode,
    troubleShootingMode,
    voterInfo,
    votes,
  } = props;
  const {
    currView,
    handleBack,
    setTimeZone,
    timeZone,
  } = useContext(ApptsContext);
  const {
    confirmationState,
  } = useContext(BookingFormContext);
  const {t} = useTranslation();

  const navigate = useNavigate();
  const {isHost} = useContext(HostContext);

  const appt = useSelector((s) => s.bookAppointments.appt);
  const apptDays = useSelector((s) => s.bookAppointments.apptDays);
  const apptDaysData = useSelector((s) => selectMonthAvailability(s, timeZone));
  const apptSmsEnabled = appt?.smsNotification || appt?.customNotifications?.attendeeSmsReminder?.enabled;
  const loadingDays = apptDaysData?.some((week) => week.loading);
  const viewingMonth = useSelector((s) => s.bookAppointments.viewingMonth);

  const initialDataLoading = useSelector((s) => s.bookAppointments.loadingInitialData || !s.bookAppointments.appt);

  const currentTimeCutoff = useSelector((s) => s.bookAppointments.currentTimeCutoff);
  const selectedDate = useSelector((s) => s.bookAppointments.selectedDate);
  const createrDisplayName = useSelector((s) => s.bookAppointments.createrDisplayName);
  const status = appt?.status;
  const isCancelled = status === EVENT_STATUS.CANCELLED;
  const isExpired = getApptEndDate(appt, appt?.start?.timeZone) < new Date();

  const isCciAccount = isCciCalendar(appt?.organizer?.email);

  // Backwards compatibility: if appt.active is undefined, treat it as active
  const isInactive = (appt?.active === false);

  return (
    <>
      {(isHost) &&
        <BreadCrumb onClick={() => {
          navigate(-1);
        }}>
          <ArrowBackIos style={{fontSize: '10px'}} /> {t('landing.backToBookingsNav')}
        </BreadCrumb>
      }
      <EventContainer>
        {[VIEW_STATE.SLOT_VIEW,
          VIEW_STATE.FORM_VIEW,
          VIEW_STATE.CONFIRM_VIEW].includes(currView) &&
          !confirmationState?.event?.originalEvent?.fromSingleUseLink && <NavButton
          aria-label="Previous Step"
          key="nav-prev-step"
          variant="outlined" size="medium" color="inherit"
          onClick={handleBack}
        >
          <SvgIcon fontSize="small" component={() => (<div><ChevronLeftFilled /></div>)} inheritViewBox />
        </NavButton>}
        {(initialDataLoading || errorInfo.isError) ? (
          (errorInfo.isError) ? (
            <ErrorDisplay errorInfo={errorInfo}/>
          ) : (
            <LoadingContainer>
              <ZmSpinner size="80" absolute/>
            </LoadingContainer>
          )
        ) : (
          (isCancelled || isExpired || isInactive) ?
          (isExpired) ?
            <MessageDisplay
              appointment={appt}
              header={t('cancellation.cancelEventExpiredHeader')}
              body={t('cancellation.cancelEventExpiredMsg')}
              displayName={createrDisplayName}
              isCciAccount={isCciAccount}
              isExpired={isExpired}
            /> :
            <MessageDisplay
              header={t('error.errorMsgHeader404')}
              body={t('error.errorMsgBody404')}
              isCancelled={isCancelled || isInactive}
              isCciAccount={isCciAccount}
            /> :
          <Fade in={true}>
            <EventStack>
              {[VIEW_STATE.DATE_VIEW].includes(currView) && <SidebarContainer>
                <ApptInfo
                  appt={appt}
                  hideAvatar={isCciAccount || isAnonymousTeamAppt(appt)}
                  toCompletedScreen={false}
                />
                <DateSection>
                  <TimeZonePicker
                    sx={{
                      marginLeft: '4px',
                      marginBottom: '4px',
                    }}
                    locked={appt?.lockTimezoneEnabled}
                    value={timeZone} onChange={setTimeZone} />

                  <BookingDatePicker
                    // Disable Today button for mobile
                    // displayTodayBtn={
                    //   toDateTime(getMinDate(appt), timeZone).startOf('day').valueOf() <= todayDt.valueOf()
                    // }
                    inline
                    formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
                    onSelect={handleSelectDate}
                    onMonthChange={(date) => {
                      onMonthChange(date);
                    }}
                    selected={new Date(selectedDate)}
                    loadingMonth={loadingDays && !isTeamAppt(appt)}
                    disableNextMonth={loadingDays && isTeamAppt(appt)}
                    filterDate={(date) => {
                      const weekData = apptDays?.[getWeekAvailabilityKey(toDateTime(date, timeZone), timeZone)];
                      return (
                        isInApptSpanRange(date, timeZone, appt, currentTimeCutoff) &&
                          (!isTeamAppt(appt) ||
                            !(
                              weekData?.loading &&
                              toDateTime(viewingMonth, timeZone, true).endOf('month').startOf('day')
                                .diff(toDateTime(date, timeZone, true)).milliseconds < 0
                            )
                          )
                      );
                    }}
                    minDate={getMinDate(appt)}
                    dayClassName={(date) => getDayClassName(date, selectedDate, appt,
                      toDateTime(currentTimeCutoff, timeZone), apptDays)}
                    maxDate={getApptEndDate(appt, timeZone).toJSDate()}
                  />
                </DateSection>
                {!isCciAccount && <PoweredBy />}
                <PolicyTerms />
              </SidebarContainer>}
              <MainContainer>
                {(currView === VIEW_STATE.SLOT_VIEW) && <>
                  <TypographyH5
                    sx={{
                      textAlign: 'center',
                      marginBottom: '4px',
                    }}
                  >
                    {appt.summary || t('common.noTitle')}&nbsp;
                  </TypographyH5>

                  <TimeZonePicker
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: '4px',
                    }}
                    locked={appt?.lockTimezoneEnabled}
                    value={timeZone} onChange={setTimeZone} />

                  <Divider sx={{marginTop: '8px'}} />
                  <DatesWindow
                    onSelectSlot={handleSelectSlot}
                    calendarId={calendarId}
                    selectedSlot={selectedSlot}
                    apptCap={appt.capacity}
                    timeZone={timeZone}
                    isBookerVoting={isBookerVoting}
                    votes={votes}
                    setCurrView={setCurrView}
                    voterInfo={voterInfo}
                    troubleShootingMode={troubleShootingMode}
                    hasTroubleShootingMode={isHost}
                  />
                  {
                    isBookerVoting &&
                    <VotingFooter
                      isBookerVoting={isBookerVoting}
                      setCurrView={setCurrView}
                      votes={votes}
                    />
                  }
                  {
                    isHost &&
                    <DebugFooter
                      troubleShootingMode={troubleShootingMode}
                      setTroubleShootingMode={setTroubleShootingMode}
                    />
                  }
                  {!isCciAccount && <PoweredBy />}
                  <PolicyTerms />
                </>}
                {[VIEW_STATE.FORM_VIEW,
                  VIEW_STATE.CONFIRM_VIEW].includes(currView) && <>
                  <BookingFinalizer
                    appointment={appt}
                    appointmentSmsEnabled={apptSmsEnabled}
                    calendarId={calendarId}
                    startDateTime={selectedSlot?.start}
                  />
                  {!isCciAccount && <PoweredBy />}
                  <PolicyTerms />
                </>}
              </MainContainer>
            </EventStack>
          </Fade>
        )}
      </EventContainer>
    </>
  );
}
